import { useMemo } from 'react';

import {
  type BannerClassStudentDropDownViewModel,
  useGetApiV1BannerClassStudents,
} from '@/shared/api/generated';
import { useDebouncedFilter } from '@/shared/ui/molecules/LookupAutocomplete';
import { unwrapPagedResponse } from '@/shared/utils/unwrapPagedResponse';

/** Turma oferecida na busca, já com a marca que o endpoint devolve. */
export interface BannerClassStudentOption {
  readonly id: number;
  readonly label: string;
  readonly brandName: string | undefined;
}

/** Itens por página da busca de turmas. */
const CLASS_STUDENTS_PAGE_SIZE = 20;

/**
 * Busca de turmas do formulário de Banner, com os filtros que o endpoint
 * dedicado oferece: texto, marcas e "somente turmas ativas".
 *
 * Consome `GET /api/v1/banner/class-students` — diferente do lookup genérico de
 * `classroomdiscipline`, este devolve `brandId`/`brandName` e aceita
 * `BrandIds`/`ActiveOnly`, que é o que os chips de marca e o checkbox do
 * protótipo precisavam para funcionar de verdade.
 *
 * @param filter - Texto digitado (debounce + mínimo de caracteres).
 * @param brandIds - Marcas selecionadas; vazio considera todas.
 * @param activeOnly - Restringe às turmas ativas (padrão do backend é `true`).
 * @returns `{ options, isLoading, isQuerySuccess }`.
 */
export function useBannerClassStudentSearch(
  filter = '',
  brandIds: readonly number[] = [],
  activeOnly = true,
) {
  const { debouncedFilter, isReady } = useDebouncedFilter(filter);

  const { data, isLoading, isSuccess } = useGetApiV1BannerClassStudents(
    isReady
      ? {
          Filter: debouncedFilter,
          ...(brandIds.length > 0 ? { BrandIds: [...brandIds] } : {}),
          ActiveOnly: activeOnly,
          Page: 1,
          PageSize: CLASS_STUDENTS_PAGE_SIZE,
        }
      : undefined,
    {
      query: {
        enabled: isReady,
        // Busca de lookup não guarda cache: com `gcTime` default, repetir um texto
        // já buscado devolvia a resposta guardada **sem pedir nada ao servidor** —
        // e o operador que trocou de marca no meio via a lista de antes, como se o
        // filtro não tivesse sido aplicado. Zerado, toda combinação de texto +
        // filtros vale uma requisição nova.
        gcTime: 0,
        staleTime: 0,
      },
    },
  );

  const options = useMemo<BannerClassStudentOption[]>(
    () =>
      unwrapPagedResponse<BannerClassStudentDropDownViewModel>(data)
        .filter(item => item.id !== undefined)
        .map(item => ({
          id: item.id as number,
          label: item.code ? `${item.name ?? ''} · ${item.code}`.trim() : (item.name ?? ''),
          brandName: item.brandName ?? undefined,
        })),
    [data],
  );

  return { options, isLoading, isQuerySuccess: isSuccess } as const;
}
